//
// Tracer — "Resolve" (borrowed from: the trace, accumulating).
//
// Read left to right this is evidence accumulating: five elements growing from a single
// tick into a solid bar. It ends in certainty rather than beginning in it — a process
// arriving at a number, not a diagram of a mechanism.
//
// - The growth — widths 1.5, 2.5, 4, 6, 12, each roughly 1.6x the last, so the
//   acceleration reads as deliberate rather than arbitrary.
// - The constant gaps — every gap is exactly 2 units. Holding one variable fixed is
//   what makes the other legible; vary both and it becomes noise.
// - The terminal bar — 35.3% of the whole run and the only part that survives at
//   favicon size. Everything else is approach.
//
// Proportions are load-bearing. Change one and change the others to match.
//
// The PROPORTIONS are the mark; the colours are not. Every fill below is a brand token,
// so a white-label organization that sets a palette but supplies no logo of its own gets
// this mark in its own colours rather than Tracer's navy floating in someone else's
// header. An organization that supplies a logo never renders this at all — see
// `Brand.tsx`.
//

type Variant = 'full' | 'compact' | 'reversed' | 'mono'

export function TracerLogo({ size = 40, variant = 'full' }: { size?: number; variant?: Variant }) {
  const block =
    variant === 'reversed'
      ? 'var(--color-on-primary)'
      : variant === 'mono'
        ? 'var(--color-ink)'
        : 'var(--color-primary)'

  const branch =
    variant === 'reversed'
      ? 'var(--color-beam)'
      : variant === 'mono'
        ? 'var(--color-ink)'
        : 'var(--color-accent)'

  // Below 24px the fine detail turns to mud: three elements, not five.
  if (variant === 'compact' || size < 24) {
    return (
      <svg width={size} height={size} viewBox="0 0 40 40" fill="none" role="img" aria-label="Tracer">
        <rect x="4" y="16" width="3" height="8" rx="1.5" fill={block} />
        <rect x="10" y="16" width="7" height="8" rx="2" fill={block} />
        <rect x="20" y="16" width="16" height="8" rx="2" fill={branch} />
      </svg>
    )
  }

  return (
    <svg width={size} height={size} viewBox="0 0 40 40" fill="none" role="img" aria-label="Tracer">
      <rect x="3" y="16.5" width="1.5" height="7" rx="0.75" fill={block} />
      <rect x="6.5" y="16.5" width="2.5" height="7" rx="1.25" fill={block} />
      <rect x="11" y="16.5" width="4" height="7" rx="1.5" fill={block} />
      <rect x="17" y="16.5" width="6" height="7" rx="1.5" fill={block} />
      <rect x="25" y="16.5" width="12" height="7" rx="1.5" fill={branch} />
    </svg>
  )
}

/*
  The horizontal lockup — mark then wordmark, clear space equal to the stroke weight on
  all four sides — lives in `Brand.tsx` as `BrandLockup`, because the word in it is not
  always "Tracer". The arrangement is unchanged and still settled; only the source of the
  word moved.
*/

/*
  Vertical lockup — PROVISIONAL.

  The mark is settled; this arrangement is not. Placed beneath the word a horizontal
  streak stops reading as a mark and starts reading as a RULE, which means it has to be
  markedly lighter than the mark ever is on its own — at its natural 4.86:1 aspect
  stretched to the width of "Tracer" it would stand 65% of the cap height, which is a
  mark parked under a word rather than a lockup.

  This implements the brand page's "Full width · medium" preset, the one it calls the
  most likely answer: the streak spans exactly the width of the word, at 19% of the font
  size, with a 22% gap. Revisit when the arrangement is decided.

  Built from positioned elements rather than an SVG so the streak can span the measured
  width of the word without measuring it in JavaScript. `border-radius: 9999px` is
  self-clamping to half the smaller dimension, which is exactly the rx rule the brand
  page derives — the thin leading bars stay pills, the terminal bar stays a rounded
  rectangle.
*/
const STREAK_RUNS: { left: string; width: string; terminal?: boolean }[] = [
  { left: '0%', width: '4.4118%' },
  { left: '10.2941%', width: '7.3529%' },
  { left: '23.5294%', width: '11.7647%' },
  { left: '41.1765%', width: '17.6471%' },
  // 35.3% of the run — the only part that survives at the smallest sizes.
  { left: '64.7059%', width: '35.2941%', terminal: true },
]

export function TracerLockupStacked({
  size = 40,
  onDark = false,
  showTagline = false,
  productName = 'Tracer',
  tagline = 'Cost Allocation',
  waiting = false,
}: {
  size?: number
  onDark?: boolean
  showTagline?: boolean
  /**
   * The word over the streak. A white-label organization that has set a name but no mark
   * of its own gets this lockup with its name in it — the streak is a graphic device, not
   * a letterform, so it carries another word without becoming a different mark.
   */
  productName?: string
  tagline?: string
  /**
   * Lights the bars left to right, on a loop, while something is loading.
   *
   * The mark already reads as a process arriving at a number, so this animates what it
   * was drawn to mean rather than adding a spinner next to it. Purely decorative: the
   * streak is <c>aria-hidden</c>, and whatever is being waited for says so in text.
   * Removed entirely under <c>prefers-reduced-motion</c> — see brand.css.
   */
  waiting?: boolean
}) {
  const block = onDark ? 'var(--color-on-primary)' : 'var(--color-primary)'
  const terminal = onDark ? 'var(--color-beam)' : 'var(--color-accent)'

  return (
    <span className="inline-flex flex-col items-stretch">
      <span
        style={{
          fontFamily: 'var(--font-display)',
          fontWeight: 800,
          letterSpacing: '-0.045em',
          lineHeight: 0.95,
          fontSize: size,
          whiteSpace: 'nowrap',
          color: onDark ? 'var(--color-on-primary)' : 'var(--color-primary)',
        }}
      >
        {productName}
      </span>

      <span
        aria-hidden
        style={{
          position: 'relative',
          display: 'block',
          width: '100%',
          height: size * 0.19,
          marginTop: size * 0.22,
        }}
      >
        {STREAK_RUNS.map((run, index) => (
          <span
            key={run.left}
            className={waiting ? 'streak-run streak-run--waiting' : 'streak-run'}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: run.left,
              width: run.width,
              borderRadius: 9999,
              background: run.terminal ? terminal : block,
              /*
                The stagger is what makes this a sequence rather than five bars blinking
                at once. 110ms against a 1.5s cycle puts the terminal bar's peak around
                710ms — late enough to read as the arrival, early enough that the pause
                afterwards is still obvious.
              */
              animationDelay: waiting ? `${index * 0.11}s` : undefined,
            }}
          />
        ))}
      </span>

      {showTagline && (
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: size * 0.19,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            opacity: 0.6,
            marginTop: size * 0.2,
            color: onDark ? 'var(--color-on-primary)' : 'var(--color-muted)',
          }}
        >
          {tagline}
        </span>
      )}
    </span>
  )
}

/**
 * App icon / favicon / avatar. Primary tile, mark reversed out of it.
 *
 * The one place raw hexes are still correct: this is the Tracer app icon as an asset, and
 * it is used where there is no document to inherit a custom property from — exported,
 * pasted into a store listing, or copied into `index.html` as the shipped favicon. A
 * white-label organization does not render this; it supplies `logo.iconUrl` and
 * `applyTheme` swaps the tab icon for it.
 */
export function TracerIcon({ size = 64 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none" role="img" aria-label="Tracer">
      <rect width="64" height="64" rx="14" fill="#0B3556" />
      <g transform="translate(6.4 6.4) scale(1.28)">
        <rect x="3" y="16.5" width="1.5" height="7" rx="0.75" fill="#FFFFFF" />
        <rect x="6.5" y="16.5" width="2.5" height="7" rx="1.25" fill="#FFFFFF" />
        <rect x="11" y="16.5" width="4" height="7" rx="1.5" fill="#FFFFFF" />
        <rect x="17" y="16.5" width="6" height="7" rx="1.5" fill="#FFFFFF" />
        <rect x="25" y="16.5" width="12" height="7" rx="1.5" fill="#3498CE" />
      </g>
    </svg>
  )
}
